/**
 * Mapping between the localized URL slug that appears in
 *   /hinnakiri/<slug>
 * and the internal pricingGroups filter used by the Pricing page.
 *
 * Each section defines its slug in three languages:
 *   ET:  /hinnakiri/diabeedi-uuringud
 *   RU:  /ru/цены/исследования-диабета
 *   EN:  /en/pricing/diabetes-tests
 *
 * Sections still look up the same MongoDB `pricingGroups` regardless of
 * language — only the URL differs. The `slugs.et` value is the canonical
 * id used everywhere internally.
 *
 * Off-language URLs (e.g. `/en/pricing/kilpnaarme-uuringud` — ET slug in
 * EN URL) are handled by Pricing.jsx: `getSectionBySlug()` accepts any of
 * the 3 localised slugs, and the page then 301-redirects to the correct
 * language canonical.
 */

import { pagePath } from "./routes";

export const PRICING_SECTIONS = [
  {
    slugs: {
      et: "diabeedi-uuringud",
      ru: "исследования-диабета",
      en: "diabetes-tests",
    },
    serviceId: "diabetes-tests",
    groups: ["endocrinologist", "diabetes"],
    title: {
      et: "Diabeedi uuringud",
      ru: "Исследования по диабету",
      en: "Diabetes investigations",
    },
  },
  {
    slugs: {
      et: "kilpnaarme-uuringud",
      ru: "исследования-щитовидной-железы",
      en: "thyroid-tests",
    },
    serviceId: "thyroid-tests",
    groups: ["endocrinologist", "thyroid"],
    title: {
      et: "Kilpnäärme uuringud",
      ru: "Исследования щитовидной железы",
      en: "Thyroid investigations",
    },
  },
  {
    slugs: {
      et: "muud-uuringud",
      ru: "прочие-исследования",
      en: "other-tests",
    },
    serviceId: "other-tests",
    groups: ["endocrinologist", "other", "endocrinologist-paid"],
    title: {
      et: "Muud uuringud",
      ru: "Прочие исследования",
      en: "Other investigations",
    },
  },
  {
    slugs: {
      et: "diabeedioe-teenused",
      ru: "услуги-медсестры-по-диабету",
      en: "diabetes-nurse-services",
    },
    serviceId: "diabetes-nurse",
    groups: ["diabetes-nurse-insured", "diabetes-nurse-paid"],
    title: {
      et: "Diabeediõe teenused",
      ru: "Услуги медсестры по диабету",
      en: "Diabetes nurse services",
    },
  },
  {
    slugs: {
      et: "jalaoe-teenused",
      ru: "услуги-медсестры-по-стопе",
      en: "foot-nurse-services",
    },
    serviceId: "foot-nurse",
    groups: ["foot-nurse-insured", "foot-nurse-paid"],
    title: {
      et: "Jalaõe teenused",
      ru: "Услуги медсестры по стопе",
      en: "Foot nurse services",
    },
  },
];

/**
 * Legacy underscore slugs (e.g. `diabeedi_uuringud`) were never deployed to
 * production, so no legacy redirect map is needed. All slugs are hyphenated
 * from day one.
 */

// Build fast lookups: canonical id (== slugs.et) → section, any-lang slug → id
const BY_ID = Object.fromEntries(PRICING_SECTIONS.map((s) => [s.slugs.et, s]));
const BY_SERVICE = Object.fromEntries(PRICING_SECTIONS.map((s) => [s.serviceId, s]));
const BY_ANY_SLUG = {};
for (const s of PRICING_SECTIONS) {
  for (const localized of Object.values(s.slugs)) BY_ANY_SLUG[localized] = s.slugs.et;
}

/**
 * Look up a section by any of its 3 localised slugs.
 * (Returns the full section object; use `section.slugs[lang]` to build URLs.)
 */
export function getSectionBySlug(slug) {
  const id = BY_ANY_SLUG[slug];
  return id ? BY_ID[id] : null;
}

export function getSectionByServiceId(serviceId) {
  return BY_SERVICE[serviceId] || null;
}

/**
 * Reverse-lookup: given a comma-separated `show=` query string from the
 * OLD URL scheme (`/hinnakiri?show=endocrinologist,diabetes`), find which
 * pricing section that filter corresponds to. Used for 301-style redirects
 * from legacy URLs Google may have indexed.
 */
export function getSectionByShowParam(showParam) {
  if (!showParam) return null;
  const ids = showParam.split(",").map((s) => s.trim()).filter(Boolean).sort().join(",");
  return PRICING_SECTIONS.find((s) => [...s.groups].sort().join(",") === ids) || null;
}

/**
 * Build the localized pricing URL for a section id (== slugs.et) or a
 * full section object.
 *   pricingSectionUrl("diabeedi-uuringud", "et") -> "/hinnakiri/diabeedi-uuringud"
 *   pricingSectionUrl("diabeedi-uuringud", "ru") -> "/ru/цены/исследования-диабета"
 *   pricingSectionUrl("diabeedi-uuringud", "en") -> "/en/pricing/diabetes-tests"
 */
export function pricingSectionUrl(idOrSlug, lang) {
  // Derive base path from the central route registry so RU/EN prefixes and
  // the "pricing" slug per language never drift out of sync.
  const pricingBase = pagePath("pricing", lang);
  if (!idOrSlug) return pricingBase;

  // Accept both canonical id (== slugs.et) and any localised slug
  const section = BY_ID[idOrSlug] || BY_ID[BY_ANY_SLUG[idOrSlug]];
  if (!section) return pricingBase;
  return `${pricingBase}/${section.slugs[lang]}`;
}
